import { AfterViewChecked, Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { MessagingService } from 'src/app/core/messaging/messaging.service';

import { Subject } from 'rxjs';
import { startWith, takeUntil } from 'rxjs/operators';
import { MapMenuCode } from 'src/app/feature/map-viewer/map-menus/map-menu-list';
import { SidePanelStreamsService } from 'src/app/feature/map-viewer/side-panel/side-panel-streams.service';
import { SidePanelName } from 'src/app/feature/map-viewer/side-panel/side-panel.component';
import { WorkspaceType } from 'src/app/shared/map-data-services/mapWorkspace/map-workspace.types';
import { ProjectTaskMigrationStatus } from 'src/app/shared/map-data-services/project/project';
import { User, UserRole } from 'src/app/shared/user/user';
import { UserSettingsStreamService } from 'src/app/shared/user/user-settings-stream.service';
import { MapWorkspacesStoreService } from '../../current-map-workspaces/map-workspaces-store.service';
import { ProjectStreamService } from '../../current-project/project-stream.service';
import { CurrentUserStreamService } from '../../current-user/current-user-stream.service';
import { FeatureFlagName } from '../../feature-flag/feature-flag';
import { FeatureFlagStreamService } from '../../feature-flag/feature-flag-stream.service';
import { MenuService } from '../../layout/menu.service';
import { ButtonType } from '../buttons/button';
import { GspAnnouncementBannerService, GspAnnouncementStatus } from './gsp-announcement-banner.service';

interface Announcement {
    announcementId: string;
    announcementContent: string;
    announcementLink: string;
    announcementStatus: GspAnnouncementStatus;
}

@Component({
    selector: 'gsp-announcement-banner',
    templateUrl: './gsp-announcement-banner.component.html'
})
export class GspAnnouncementBannerComponent implements OnInit, OnDestroy, AfterViewChecked {
    user: User = null;
    announcements: Announcement[] = null;
    showTaskMigrationBanner: boolean = false;
    showTaskMigrationFailedBanner: boolean = false;
    showTaskMigrationCompletedBanner: boolean = false;

    private destroyed$: Subject<void> = new Subject<void>();

    ButtonType = ButtonType;
    UserRole = UserRole;
    WorkspaceType = WorkspaceType;

    @ViewChild('announcementBanner', { read: ElementRef })
    announcementBannerContainer: ElementRef;

    constructor(
        private projectStreamService: ProjectStreamService,
        private currentUserStream: CurrentUserStreamService,
        private menuService: MenuService,
        private sidePanelStreams: SidePanelStreamsService,
        private mapWorkspacesStore: MapWorkspacesStoreService,
        private announcementBannerService: GspAnnouncementBannerService,
        private featureFlagStreamService: FeatureFlagStreamService,
        private messaging: MessagingService,
        private translateService: TranslateService,
        private userSettingsStream: UserSettingsStreamService
    ) {}

    ngOnInit(): void {
        this.user = this.currentUserStream.stream.getValue();

        this.announcementBannerService.getAnnouncements().then(announcements => {
            this.announcements = announcements
                .map(res => ({
                    announcementId: res.announcement.id,
                    announcementContent:
                        res.announcement.properties[this.translateService.currentLang] || res.announcement.content,
                    announcementLink: res.announcement.url,
                    announcementStatus: res.tracking ? res.tracking.status : null
                }))
                // Only show announcements without any tracking available
                .filter(announcement => announcement.announcementStatus === null);
        });

        // Only show task migration banners for GIS workspaces
        this.mapWorkspacesStore.currentMapWorkspaceStream.pipe(takeUntil(this.destroyed$)).subscribe(workspace => {
            if (workspace && workspace.type === WorkspaceType.GIS) {
                if (this.featureFlagStreamService.flagEnabled(FeatureFlagName.TASK_MIGRATION)) {
                    this.initTaskMigrationBanners();
                }
            } else {
                // Clean up any existing banners when not in GIS workspace
                this.closeTaskMigrationBanner();
            }
        });
    }

    ngAfterViewChecked(): void {
        this.updateToastOffset();
    }

    ngOnDestroy(): void {
        this.destroyed$.next(null);
    }

    private initTaskMigrationBanners(): void {
        let previousStatus: string = null;

        this.projectStreamService.taskMigrationStatusStream
            .pipe(startWith(null), takeUntil(this.destroyed$))
            .subscribe(status => {
                const projectSettings = this.userSettingsStream.getCurrentProjectSettings();
                this.showTaskMigrationBanner = status === ProjectTaskMigrationStatus.NOT_FOUND;
                const isFailedStatus =
                    status &&
                    status !== ProjectTaskMigrationStatus.NOT_FOUND &&
                    status !== ProjectTaskMigrationStatus.COMPLETED &&
                    status !== ProjectTaskMigrationStatus.IN_PROGRESS &&
                    status !== ProjectTaskMigrationStatus.PENDING;
                this.showTaskMigrationFailedBanner =
                    isFailedStatus && !projectSettings?.taskMigrationFailedBannerDismissed;

                if (
                    previousStatus &&
                    (previousStatus === ProjectTaskMigrationStatus.IN_PROGRESS ||
                        previousStatus === ProjectTaskMigrationStatus.PENDING) &&
                    status === ProjectTaskMigrationStatus.COMPLETED
                ) {
                    this.showTaskMigrationCompletedBanner = true;
                }

                previousStatus = status;
            });
    }

    public dismissAnnouncement(announcementId: string): void {
        this.announcements = this.announcements.filter(notification => notification.announcementId !== announcementId);
        // only track user action for non shared workspaces
        if (!this.mapWorkspacesStore.currentMapWorkspaceStream.getValue().isPubliclySharedMapWorkspace) {
            this.announcementBannerService.createAnnouncementTracking(announcementId, GspAnnouncementStatus.DISMISSED);
        }
        this.updateToastOffset();
    }

    public goToLink(url: string): void {
        window.open(url, '_blank');
    }

    public updateToastOffset(): void {
        // offset announcement banner height for toast notifications
        if (this.announcementBannerContainer) {
            this.messaging.setToastVerticalOffset(this.announcementBannerContainer.nativeElement.offsetHeight);
        }
    }

    // Banner Actions for Task Migration.

    public startMigration() {
        this.clearTaskMigrationFailedBannerDismissed();
        this.projectStreamService.startTaskMigration();
        this.closeTaskMigrationBanner();

        // Close task panel and task details panel
        const openPanels = this.menuService.activeMenusStream.getValue();
        const updatedPanels = openPanels.filter(panel => panel !== MapMenuCode.LEGACY_TASKS);
        if (updatedPanels.length !== openPanels.length) {
            this.menuService.activeMenusStream.next(updatedPanels);
        }

        this.sidePanelStreams.closeSidePanel(SidePanelName.LEGACY_TASK_DETAIL);
    }

    public closeTaskMigrationBanner(userDismissed: boolean = false) {
        const isFailedBanner = this.showTaskMigrationFailedBanner;
        this.showTaskMigrationBanner = false;
        this.showTaskMigrationCompletedBanner = false;
        this.showTaskMigrationFailedBanner = false;
        if (userDismissed && isFailedBanner) {
            this.saveTaskMigrationFailedBannerDismissed();
        }
    }

    private saveTaskMigrationFailedBannerDismissed(): void {
        // Save to user project settings that the user has dismissed the task migration failed banner, so that it doesn't show again.
        const project = this.projectStreamService.getCurrentProject();

        this.userSettingsStream.getProjectSettings(project.id).then(settings => {
            if (settings) {
                settings.taskMigrationFailedBannerDismissed = true;
                this.userSettingsStream.updateCurrentProjectSettings(settings);
            }
        });
    }

    private clearTaskMigrationFailedBannerDismissed(): void {
        // When user starts a new migration, reset the dismissed flag so the failed banner can show again if migration fails again.
        const project = this.projectStreamService.getCurrentProject();

        this.userSettingsStream.getProjectSettings(project.id).then(settings => {
            if (settings?.taskMigrationFailedBannerDismissed) {
                settings.taskMigrationFailedBannerDismissed = false;
                this.userSettingsStream.updateCurrentProjectSettings(settings);
            }
        });
    }
}
